import { Show, createEffect, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Grid } from '../../../grid-system/grid/grid';
import { Section } from '../../../grid-system/section/section';
import { AtosLogo } from '../../icons-library/atos-logo-desktop';
import { CrossIcon } from '../../icons-library/cross';
import { LoginForm } from '../../login-form/login-form';
import { StyledCloseButton, StyledCloseButtonText } from '../../menu/menu-desktop/menu-header/menu-header.styles';
import { StyledLogo, StyledLogoContent } from '../../menu/menu-desktop/shortcuts/shortcuts.styles';
import { StyledLoginMenuLeftArea, StyledLoginMenuRightArea, StyledLoginMenuHeader, StyledLoginMenuLeftBackgroundPanel, StyledSpacer, StyledWelcomeText, StyledDivider, StyledLoginInstruction, StyledEmptyColumn, StyledBottomFade } from '../login-menu.styles';
import { Heading } from '../../../ui-components/heading/heading';
import theme from '../../../style/theme';
import { TabHandler } from '../../menu/menu-desktop/tab-handling';

export const LoginMenuDesktop = (props: LoginMenuProps) => {
    const { localize, siteInfo, viewport, userState } = useContext(AppContext);
    const [ welcomeMessage, setWelcomeMessage ] = createSignal<string>('');

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    const isLargeDesktop = () => {
        return viewport.width > theme.breakpoints.DESKTOP_LARGE;
    };

    createEffect(() => {
        if (siteInfo.siteType === 'default') {
            setWelcomeMessage(localize('welcome-to-healthcare-professionals', 'Welcome Healthcare Professionals!'));
        } else {
            setWelcomeMessage(localize('welcome-to-atos-care', 'Welcome to Atos Care'));
        }
    });


    const Header = () => (
        <StyledLoginMenuHeader>
            <StyledSpacer />
            <StyledCloseButton
                onClick={() => props.handleCloseMenuClick()}
                ref={(el: HTMLElement) => setFirstTabbableElement(el)}
                aria-label={localize('close', 'Close')}
            >
                <CrossIcon fill="white" width={1.79} height={1.79} ariaHidden />
                <StyledCloseButtonText>{localize('close', 'Close')}</StyledCloseButtonText>
            </StyledCloseButton>
        </StyledLoginMenuHeader>
    );

    const WelcomeText = () => (
        <StyledWelcomeText>
            <Show when={viewport.width < theme.breakpoints.ORIGINAL_TABLET}>
                <Heading tag='h1' variant='smallGray' color={{ name: 'white', color: 'white' }}>
                    {welcomeMessage()}
                </Heading>
            </Show>
            <Show when={viewport.width >= theme.breakpoints.ORIGINAL_TABLET && viewport.width < theme.breakpoints.DESKTOP_LARGE}>
                <Heading tag='h1' variant='large' color={{ name: 'white', color: 'white' }}>
                    {welcomeMessage()}
                </Heading>
            </Show>
            <Show when={viewport.width >= theme.breakpoints.DESKTOP_LARGE}>
                <Heading tag='h1' variant='xxlarge' color={{ name: 'white', color: 'white' }}>
                    {welcomeMessage()}
                </Heading>
            </Show>
        </StyledWelcomeText>
    );

    const Logo = () => (
        <StyledLogoContent>
            <StyledLogo>
                <AtosLogo />
            </StyledLogo>
        </StyledLogoContent>
    );

    const LoginCtaText = () => (
        <Show when={!userState.user}>
            <StyledDivider />
            <StyledLoginInstruction>
                <Show
                    fallback={localize('log-in-to-access-healthcare-professionals', 'Log in to view resources for healthcare professionals')}
                    when={siteInfo.siteType === 'atos-care'}
                >
                    {localize('log-in-to-access-atos-care', 'Log in to access Atos Care')}
                </Show>
            </StyledLoginInstruction>
        </Show>
    );

    return (
        <Section
            templateShorthand={[12]}
            widthType={'bgFull'}
            removeSpacingBlock={true}
            heightType="fill"
            customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
        >
            <TabHandler
                refs={{
                    firstTabbableElement,
                    lastTabbableElement,
                }}
            >
                <Grid
                    templateShorthand={[4, 1, 7]}
                    customCss={'height: 100%;'}
                    responsive={{
                        largeDesktop: [4, 1, 7],
                        smallDesktop: [4, 8],
                        tablet: [12],
                        mobile: [12],
                    }}
                >
                    <StyledLoginMenuLeftArea>
                        <Logo />

                        <WelcomeText/>

                        <LoginCtaText />

                        <StyledLoginMenuLeftBackgroundPanel />
                    </StyledLoginMenuLeftArea>

                    <Show when={isLargeDesktop()}>
                        <StyledEmptyColumn/>
                    </Show>

                    <StyledLoginMenuRightArea>
                        <Header/>

                        <LoginForm
                            firstElement={(el: HTMLElement) => {
                                setFirstTabbableElement(el);
                            }}
                            lastElement={(el: HTMLElement) => {
                                setLastTabbableElement(el);
                            }}
                        />

                        <StyledBottomFade />
                        
                    </StyledLoginMenuRightArea>
                </Grid>
            </TabHandler>
        </Section>
    );
};
export type LoginMenuProps = {
    handleCloseMenuClick: () => void;
    handleSignOutClick: () => void;
};
